import * as React from 'react';

import Checkbox from 'components/checkbox';
import Spinner from 'components/spinner/spinner.component';
import { Icon, ICON_NAME } from 'components/icon';
import { FRFriendListVirtualized } from '../fr-friend-list-virtualized';
import { FRFriendListSimple } from '../fr-friend-list-simple';
import { FRFriendListNoFriends } from '../fr-friend-list-no-friends';
import { FRFriendListFeedback } from '../fr-friend-list-feedback';
import { translate } from 'components/translate-hoc';

import { FR_FRIEND_LIST_TEXT_KEYS, INITIAL_FRIENDS } from '../fr-friend-list-common';
import './fr-friend-list-ui.styles.less';
import { IFRFriendListUIProps } from './fr-friend-list-ui.types';
import { getModeClass } from 'utils/css';

export class FRFriendListUI extends React.Component<IFRFriendListUIProps> {
    public getExpandFriendsButton(): JSX.Element | null {
        if (this.props.friends.length <= INITIAL_FRIENDS || this.props.isFriendListExpanded) {
            return null;
        }

        const { dangerouslyTranslateWithHTML: translateKey, shouldInheritStyles } = this.props;
        const amount: number = this.props.friends.length - INITIAL_FRIENDS;
        const caption: string = translateKey(FR_FRIEND_LIST_TEXT_KEYS.SHOW_MORE, { amount });

        return (
            <div className={`${getModeClass('sitegen-fr-friend-list', shouldInheritStyles)}__expand-button`}
                onClick={this.props.onExpandListClick}>
                {caption}
                <Icon name={ICON_NAME.ANGLE_DOWN} />
            </div>
        );
    }

    public getFriendList(): JSX.Element {
        if (this.props.isFriendListExpanded) {
            return (
                <FRFriendListVirtualized
                    env={this.props.env}
                    friends={this.props.friends}
                    listOfCheckedFriends={this.props.listOfCheckedFriends}
                    onCheck={this.props.onCheckFriend}
                    shouldInheritStyles={this.props.shouldInheritStyles}
                />
            );
        }

        return (
            <FRFriendListSimple
                shouldInheritStyles={this.props.shouldInheritStyles}
                env={this.props.env}
                friends={this.props.friends}
                listOfCheckedFriends={this.props.listOfCheckedFriends}
                onCheck={this.props.onCheckFriend}
            />
        );
    }

    public getLoader(): JSX.Element {
        const { dangerouslyTranslateWithHTML: translateKey, shouldInheritStyles } = this.props;
        const basePageStyleClass = getModeClass('sitegen-fr-friend-list', shouldInheritStyles);

        return (
            <div className={`${basePageStyleClass} ${basePageStyleClass}--loading`}>
                <div className={`${basePageStyleClass}__header`}>
                    <div className={`${basePageStyleClass}__title`}>
                        {translateKey(FR_FRIEND_LIST_TEXT_KEYS.INVITE_FRIENDS)}
                    </div>
                </div>

                <Spinner />
            </div>
        );
    }

    public getFriendListUI(): JSX.Element {
        const {
            dangerouslyTranslateWithHTML: translateKey,
            listOfCheckedFriends,
            maxAvailableFriendsToInvite,
            inviting,
            shouldInheritStyles
        } = this.props;

        const areAllFriendsChecked: boolean = listOfCheckedFriends.size > 0
            && listOfCheckedFriends.size === maxAvailableFriendsToInvite;
        const contentOfInviteButton: React.ReactNode = inviting
            ? translateKey(FR_FRIEND_LIST_TEXT_KEYS.LOADING)
            : translateKey(FR_FRIEND_LIST_TEXT_KEYS.INVITE_FRIENDS);
        const basePageStyleClass = getModeClass('sitegen-fr-friend-list', shouldInheritStyles);

        const inviteButtonClassList: string[] = [`${basePageStyleClass}__action-button`];
        if (inviting) {
            inviteButtonClassList.push(`${basePageStyleClass}__action-button--loading`);
        }

        return (
            <div className={basePageStyleClass}>
                <div className={`${basePageStyleClass}__header`}>
                    <div className={`${basePageStyleClass}__title`}>
                        {translateKey(FR_FRIEND_LIST_TEXT_KEYS.INVITE_FRIENDS)}
                    </div>
                    <Checkbox
                        shouldInheritStyles={shouldInheritStyles}
                        label={translateKey(FR_FRIEND_LIST_TEXT_KEYS.SELECT_ALL)}
                        checked={areAllFriendsChecked}
                        onChange={this.props.onCheckAll}
                    />
                </div>

                {this.getFriendList()}

                <div className={`${basePageStyleClass}__footer`}>
                    {this.getExpandFriendsButton()}

                    <div className={inviteButtonClassList.join(' ')} onClick={this.props.onInviteClick}>
                        {contentOfInviteButton}
                    </div>
                </div>
            </div>
        );
    }

    public render(): JSX.Element | null {
        if (!this.props.ready) {
            return this.getLoader();
        }

        if (this.props.isFeedbackOpen) {
            return <FRFriendListFeedback
                error={this.props.error}
                shouldInheritStyles={this.props.shouldInheritStyles}
                onClose={this.props.onFeedbackClose} />;
        }

        if (this.props.friends.length === 0) {
            return <FRFriendListNoFriends shouldInheritStyles={this.props.shouldInheritStyles} />;
        }

        return this.getFriendListUI();
    }
}

export const FRFriendListUIWithTranslations = translate()(FRFriendListUI);
